import type { AgendaItem } from "@elation/api-client/src/stitch-web/models/agendaItem";
import { AgendaItemSignalState } from "@elation/api-client/src/stitch-web/models/agendaItemSignalState";
import { AgendaItemStatus } from "@elation/api-client/src/stitch-web/models/agendaItemStatus";

import { parseNullableDate } from "@src/shared/utils/dates";

type AgendaItemDateField =
  | "completedAt"
  | "discussedAt"
  | "assessedAt"
  | "plannedAt"
  | "followUpAt"
  | "createdAt"
  | "updatedAt";
type MaybeAgendaItem = AgendaItem | null | undefined;

const STATUS_LABELS: Record<AgendaItem["status"], string> = {
  [AgendaItemStatus.suggested]: "Suggested",
  [AgendaItemStatus.open]: "Open",
  [AgendaItemStatus.actively_discussing]: "Actively discussing",
  [AgendaItemStatus.finalized]: "Finalized",
  [AgendaItemStatus.rejected]: "Rejected",
  [AgendaItemStatus.cancelled]: "Cancelled",
  [AgendaItemStatus.snoozed]: "Snoozed",
};

const SIGNAL_STATE_LABELS: Record<AgendaItem["signalState"], string> = {
  [AgendaItemSignalState.open]: "Open",
  [AgendaItemSignalState.discussed]: "Discussed",
  [AgendaItemSignalState.assessed]: "Assessed",
  [AgendaItemSignalState.planned]: "Planned",
  [AgendaItemSignalState.follow_up]: "Follow-up",
};

function compareDatesDesc(left: Date | null, right: Date | null): number {
  if (left && right) return right.getTime() - left.getTime();
  if (left) return -1;
  if (right) return 1;
  return 0;
}

export const AgendaItemEntity = {
  isCompleted(item: MaybeAgendaItem): boolean {
    if (!item) return false;
    return (
      item.status === AgendaItemStatus.finalized ||
      item.signalState === AgendaItemSignalState.planned
    );
  },

  isPlanned(item: MaybeAgendaItem): boolean {
    if (!item) return false;
    return (
      item.signalState === AgendaItemSignalState.planned ||
      Boolean(parseNullableDate(item.plannedAt))
    );
  },

  needsFollowUp(item: MaybeAgendaItem): boolean {
    if (!item) return false;
    return (
      !AgendaItemEntity.isCompleted(item) &&
      (item.signalState === AgendaItemSignalState.follow_up ||
        Boolean(parseNullableDate(item.followUpAt)))
    );
  },

  isActionable(item: MaybeAgendaItem): boolean {
    if (!item) return false;
    return (
      item.status !== AgendaItemStatus.rejected &&
      item.status !== AgendaItemStatus.cancelled &&
      item.status !== AgendaItemStatus.snoozed
    );
  },

  statusLabel(item: MaybeAgendaItem): string | null {
    if (!item) return null;
    return STATUS_LABELS[item.status];
  },

  signalStateLabel(item: MaybeAgendaItem): string | null {
    if (!item) return null;
    return SIGNAL_STATE_LABELS[item.signalState];
  },

  listItemLabel(item: MaybeAgendaItem): string | null {
    if (!item) return null;
    return `${item.description} — ${
      AgendaItemEntity.isCompleted(item) ? "completed" : "open"
    }`;
  },

  dateFor(item: MaybeAgendaItem, field: AgendaItemDateField): Date | null {
    return parseNullableDate(item?.[field]);
  },

  timelineDate(item: MaybeAgendaItem): Date | null {
    if (!item) return null;
    if (item.signalState === AgendaItemSignalState.follow_up) {
      return AgendaItemEntity.dateFor(item, "followUpAt");
    }

    if (item.signalState === AgendaItemSignalState.planned) {
      return AgendaItemEntity.dateFor(item, "plannedAt");
    }

    if (item.signalState === AgendaItemSignalState.assessed) {
      return AgendaItemEntity.dateFor(item, "assessedAt");
    }

    if (item.signalState === AgendaItemSignalState.discussed) {
      return AgendaItemEntity.dateFor(item, "discussedAt");
    }

    return (
      AgendaItemEntity.dateFor(item, "updatedAt") ??
      AgendaItemEntity.dateFor(item, "createdAt")
    );
  },

  compareByTimelineDesc(left: AgendaItem, right: AgendaItem): number {
    const byTimeline = compareDatesDesc(
      AgendaItemEntity.timelineDate(left),
      AgendaItemEntity.timelineDate(right),
    );

    if (byTimeline !== 0) return byTimeline;
    return left.id - right.id;
  },
} as const;

export type { AgendaItemDateField };
